import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Plus,
  ClipboardList,
  SquareCheckBig,
  Library,
  Book,
  Sparkles,
  Folder,
  GraduationCap,
  Users,
  BarChart3,
  History,
  User,
  Settings,
} from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from 'components/ui/command';
import * as AuthModule from 'modules/auth';
import { useFeature, FEATURES } from 'modules/entitlements';
import { PERMISSIONS, type Permission } from 'modules/auth/permissions';
import { useSettingsDialog } from 'components/settings/useSettingsDialog';
import { useCommandPaletteStore } from 'store/commandPalette';

interface Command {
  /** Labels are translated and can collide or change on language switch, so they aren't safe list keys. */
  id: string;
  icon: React.ElementType;
  label: string;
  /** Route navigation target; mutually exclusive with `action`. */
  to?: string;
  /** In-place action (e.g. open a settings-dialog section). */
  action?: () => void;
  show: boolean;
}

/** Mount once near the app root. Open-state lives in `useCommandPaletteStore` so any trigger can open it. */
export const CommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { open, setOpen, toggle } = useCommandPaletteStore();
  const { open: openSettings } = useSettingsDialog();

  const {
    state: { permissions: p },
  } = AuthModule.Context.useAuthContext();
  const hasCollections = useFeature(FEATURES.COLLECTIONS);
  const hasTeams = useFeature(FEATURES.TEAMS);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggle]);

  const has = (perm: Permission) => p.includes(perm);
  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const navCommands: Command[] = [
    { id: 'tests', icon: ClipboardList, label: t('nav.tests'), to: '/app/tests', show: has(PERMISSIONS.TESTS_LIST) },
    { id: 'grading', icon: SquareCheckBig, label: t('nav.grading'), to: '/app/grading', show: has(PERMISSIONS.GRADING_GRADE) },
    { id: 'subjects', icon: Book, label: t('nav.subjects'), to: '/app/subjects', show: has(PERMISSIONS.SUBJECTS_VIEW) },
    { id: 'question_bank', icon: Library, label: t('nav.question_bank'), to: '/app/questions', show: has(PERMISSIONS.QUESTIONS_VIEW) },
    { id: 'ai_generate', icon: Sparkles, label: t('nav.ai_generate'), to: '/app/documents', show: has(PERMISSIONS.DOCUMENTS_EDIT) },
    {
      id: 'library',
      icon: Folder,
      label: t('nav.library'),
      to: '/app/library',
      show: hasCollections && has(PERMISSIONS.COLLECTIONS_LIST),
    },
    {
      id: 'groups',
      icon: GraduationCap,
      label: t('nav.groups'),
      to: '/app/teams',
      show: hasTeams && has(PERMISSIONS.TEAMS_LIST),
    },
    { id: 'members', icon: Users, label: t('nav.members'), action: () => openSettings('members'), show: has(PERMISSIONS.MEMBERS_EDIT) },
    { id: 'analytics', icon: BarChart3, label: t('nav.analytics'), to: '/app/analytics', show: has(PERMISSIONS.TESTS_VIEW_RESULTS) },
    { id: 'my_attempts', icon: History, label: t('nav.my_attempts'), to: '/app/history', show: true },
    { id: 'profile', icon: User, label: t('profile'), action: () => openSettings('profile'), show: true },
    { id: 'org_settings', icon: Settings, label: t('sidebar_orgsettings'), action: () => openSettings('org'), show: has(PERMISSIONS.ROLES_EDIT) },
  ].filter((c) => c.show);

  const canCreateTest = has(PERMISSIONS.TESTS_CREATE);

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title={t('cmdk.search_placeholder')}>
      <CommandInput placeholder={t('cmdk.search_placeholder')} />
      <CommandList>
        <CommandEmpty>{t('cmdk.no_results')}</CommandEmpty>
        {canCreateTest && (
          <CommandGroup heading={t('cmdk.actions')}>
            <CommandItem onSelect={() => go('/app/tests/create')}>
              <Plus className="h-4 w-4 text-muted-foreground" />
              <span>{t('cmdk.create_test')}</span>
            </CommandItem>
          </CommandGroup>
        )}
        <CommandGroup heading={t('cmdk.navigate')}>
          {navCommands.map((c) => (
            <CommandItem
              key={c.id}
              onSelect={() => {
                if (c.action) {
                  setOpen(false);
                  c.action();
                } else if (c.to) {
                  go(c.to);
                }
              }}
            >
              <c.icon className="h-4 w-4 text-muted-foreground" />
              <span>{c.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

/** Sidebar search pill — the primary desktop affordance. Opens the command palette. */
export const SidebarSearchButton: React.FC = () => {
  const { t } = useTranslation();
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="mb-3 flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-muted/50 px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted"
    >
      <Search className="h-4 w-4 shrink-0" aria-hidden />
      <span className="truncate">{t('cmdk.search_placeholder')}</span>
      <kbd className="ml-auto hidden shrink-0 items-center rounded border border-border bg-background px-1.5 font-mono text-[10px] sm:inline-flex">
        ⌘K
      </kbd>
    </button>
  );
};

/** Small-screen-only fallback (`md:hidden`); desktop search lives in the sidebar pill. */
export const HeaderSearchButton: React.FC = () => {
  const { t } = useTranslation();
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={t('cmdk.search_placeholder')}
      className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/50 text-muted-foreground transition-colors hover:bg-muted md:hidden"
    >
      <Search className="h-4 w-4" aria-hidden />
    </button>
  );
};

export default CommandPalette;
